import * as React from 'react';
import { FieldGroup, FieldMessage, Statuses } from '@teamsnap/teamsnap-ui';
import { useField } from 'formik';
import { PasswordToggleInput, DateTimePicker } from 'frontend-toolkit';
import { RadioGroup } from '../RadioGroup/RadioGroup';
import { MultiSelectPicker } from '../MultiSelectPicker/MultiSelectPicker';

export enum FieldType {
  FIRST_NAME = 'first_name',
  LAST_NAME = 'last_name',
  BIRTHDATE = 'birthdate',
  GENDER = 'gender',
  RELATION = 'relation',
  EMAIL = 'email',
  PHONE_NUMBER = 'phone_number',
  PHONE = 'phone',
  MULTI_SELECT = 'multi-select',
}

export enum FormFieldType {
  CHECKBOX = 'checkbox',
  DATETIME = 'datetime',
  EMAIL = 'email',
  PASSWORD_INPUT = 'input',
  PASSWORD = 'password',
  SELECT = 'select',
  TEXT = 'text',
  RADIO = 'radio',
  TEXTAREA = 'textarea',
  NUMBER = 'number',
}

type SelectOptions = {
  label: string;
  value: string;
};

type RadioOptions = {
  label: string;
  value: string;
};

export interface Props {
  required?: boolean;
  label?: string | React.ReactNode;
  mods?: string;
  name: string;
  options?: SelectOptions[] | RadioOptions[];
  placeholder?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  multiselect?: boolean;
  type: FormFieldType;
}

export const numberInputOnWheelPreventChange = (e: React.WheelEvent<HTMLInputElement>) => {
  (e.target as HTMLElement).blur();

  e.stopPropagation();
  setTimeout(() => {
    (e.target as HTMLElement).focus();
  }, 0);
};

export const FormField = ({ label, ...props }: Props) => {
  const [field, meta, helpers] = useField(props);
  const { value } = field;
  const { error, touched } = meta;
  const { setTouched, setValue } = helpers;
  const { mods, name, options, type, disabled, placeholder, required } = props;
  const hasError = touched && error;
  const classes = ['Input', hasError ? 'sui-border-red-4' : ''].join(' ');

  const textAreaClasses = [
    'sui-form-control',
    'sui-block',
    'sui-p-1',
    'sui-w-full',
    'sui-text-sm',
    'sui-rounded',
    'sui-text-gray-9',
    hasError ? 'sui-border-red-4' : 'sui-border-gray-5',
  ];

  const renderSelect = () => (
    <div className="SelectBox sui-relative">
      <select
        className={`SelectBox-options sui-font-size-5 ${classes}`}
        style={{ height: 48 }}
        id={name}
        {...field}
        disabled={disabled}
        value={value === null ? undefined : value}
      >
        <option value="">Select</option>
        {options?.map((option) => (
          <option key={option.value} label={option.label} {...field} {...props} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );

  const renderType = () => {
    let theDate;

    if (FormFieldType.DATETIME) {
      theDate = new Date(value);
      theDate = new Date(theDate.getUTCFullYear(), theDate.getUTCMonth(), theDate.getUTCDate());
    }

    const isMultiSelect = props?.multiselect ?? false;

    switch (type) {
      case FormFieldType.CHECKBOX:
        return (
          <div className="Checkbox Checkbox--inline">
            <input className="Checkbox-input" id={name} {...field} {...props} />
            <label className="Checkbox-label sui-body" htmlFor={name}>
              {label}
            </label>
          </div>
        );

      case FormFieldType.SELECT:
        return (
          <div className="SelectBox-container">
            <label htmlFor={name} className="sui-label">
              {label}
              {required && <span className="sui-text-red-40">*</span>}
            </label>

            {isMultiSelect && (
              <MultiSelectPicker options={options || []} onUpdateSelectedOptions={setValue} selected={value} />
            )}
            {!isMultiSelect && renderSelect()}
          </div>
        );

      case FormFieldType.RADIO:
        return <RadioGroup label={''} name={name} options={options} />;

      case FormFieldType.DATETIME:
        return (
          <>
            <label htmlFor={name} className="sui-label">
              {label}
              {required && <span className="sui-text-red-40">*</span>}
            </label>

            <div>
              <DateTimePicker
                id={name}
                datetime={value ? theDate : null}
                dateFormat="P"
                inputClasses=""
                placeholderText={placeholder || 'Select Date'}
                onChange={(datetime) => setValue(datetime?.toISOString())}
                onBlur={() => {
                  if (typeof value === 'undefined') {
                    setValue('');
                  }

                  setTouched(true);
                }}
                disabled={disabled}
                containerClasses={`sui-px-1 ${hasError ? 'sui-border-red-4' : ''}`}
              />
            </div>
          </>
        );

      case FormFieldType.PASSWORD_INPUT:
        return (
          <>
            <label htmlFor={name} className="sui-label">
              {label}
            </label>
            <PasswordToggleInput hasError={hasError} {...field} {...props} />
          </>
        );

      case FormFieldType.TEXTAREA:
        return (
          <>
            <label htmlFor={name} className="sui-label">
              {label}
            </label>

            <div
              className="sui-py-1 sui-block"
              style={{
                height: 80,
                marginBottom: 10, // Needed to add margin for error messages below text area
              }}
            >
              <textarea
                {...field}
                {...props}
                name={name}
                placeholder="Text"
                className={textAreaClasses.join(' ')}
                value={value === null ? undefined : value}
                style={{
                  height: 80,
                  resize: 'none',
                  fontSize: '1rem',
                  fontFamily: 'Lato',
                }}
              />
            </div>
          </>
        );

      default: {
        return (
          <>
            <label htmlFor={name} className="sui-label">
              {label}
              {required && <span className="sui-text-red-40">*</span>}
            </label>

            <input
              onWheel={numberInputOnWheelPreventChange}
              id={name}
              type={type}
              disabled={disabled}
              placeholder={typeof label === 'string' ? label : ''}
              className={`${classes} sui-h-6 sui-px-1 sui-py-1 sui-body`}
              {...field}
              value={value === null ? undefined : value}
            />
          </>
        );
      }
    }
  };

  return (
    <FieldGroup mods={mods || 'sui-mt-2'}>
      {renderType()}

      <FieldMessage mods="sui-caption" status={Statuses.ERROR}>
        {error}
      </FieldMessage>
    </FieldGroup>
  );
};
